import { c as _c } from "react/compiler-runtime";
import { BaseButton, Button } from '@7f-ui/components/button/button';
import { SimpleModal } from '@7f-ui/components/modal/simple-modal';
import { trackClickCta } from '@feat/feat-tracking';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { RatingSummary } from './ratings-summary';
import { ReviewCard } from './review-card';
import { TrustedLogo } from './trusted-logo';
import { TrustedStars } from './trusted-stars';
import type { TrustedShopsApiError, TrustedShopsData } from './types';
const TRUSTED_SHOPS_URL = 'https://www.trustedshops.de/bewertung/info_X1C5BC97973789ABD766B25F0771739D2.html';
const DAY_IN_MS = 86_400_000;
function isTrustedShopsApiError(response: TrustedShopsApiError | TrustedShopsData): response is TrustedShopsApiError {
  return 'error' in response;
}
type TrustedShopsQueryResult = {
  error: false;
  data: TrustedShopsData;
} | {
  error: true;
  message: string;
};
async function fetchTrustedShopsData(): Promise<TrustedShopsQueryResult> {
  try {
    const res = await fetch('/api/trusted/');
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- res.json() is untyped
    const response = (await res.json()) as TrustedShopsApiError | TrustedShopsData;
    if (!res.ok || isTrustedShopsApiError(response)) {
      return {
        error: true as const,
        message: isTrustedShopsApiError(response) ? response.error : 'Failed to fetch trusted shops data'
      };
    }
    return {
      error: false as const,
      data: response
    };
  } catch {
    return {
      error: true as const,
      message: 'Failed to fetch trusted shops data'
    };
  }
}
type TrustedBarProps = {
  initialData?: TrustedShopsData | null;
};
export const TrustedBar = t0 => {
  const $ = _c(33);
  const {
    initialData
  } = t0;
  const [isOpen, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["trusted-shops"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = initialData === undefined;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: fetchTrustedShopsData,
      enabled: t2,
      staleTime: DAY_IN_MS
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: queryData
  } = useQuery(t3);
  const data = initialData === undefined ? !queryData?.error && queryData?.data || null : initialData;
  let t4;
  if ($[3] !== data) {
    t4 = () => {
      trackClickCta("open reviews - header", {
        type: "reviews"
      });
      if (data) {
        setOpen(true);
      }
    };
    $[3] = data;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleOpenReviews = t4;
  let t5;
  if ($[5] !== data || $[6] !== handleOpenReviews) {
    t5 = data ? {
      onClick: handleOpenReviews
    } : {
      as: "a" as const,
      href: TRUSTED_SHOPS_URL,
      target: "_blank",
      rel: "noopener noreferrer",
      onClick: handleOpenReviews
    };
    $[5] = data;
    $[6] = handleOpenReviews;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const buttonProps = t5;
  let t6;
  if ($[8] !== data) {
    t6 = data && <>{data.score.rating}{" / 5 - "}</>;
    $[8] = data;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== buttonProps) {
    t7 = <BaseButton className="font-base-copy underline underline-offset-3 hover:no-underline" {...buttonProps}>Bewertungen</BaseButton>;
    $[10] = buttonProps;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const reviewsLabel = t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="hidden h-full items-center gap-2 md:flex"><TrustedLogo /><span className="font-base-caption">Verifiziert durch Trusted Shops</span></div>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== data) {
    t10 = data && <div className="flex items-center"><TrustedStars rating={Number(data.score.rating.toFixed())} className="size-2.5" /></div>;
    $[16] = data;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== reviewsLabel) {
    t11 = <div className="font-base-caption">{reviewsLabel}</div>;
    $[18] = reviewsLabel;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <TrustedLogo className="md:hidden" />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t10 || $[22] !== t11) {
    t13 = <div className="flex h-full flex-row items-center justify-center gap-1 text-white md:justify-between">{t9}<div className="flex items-center gap-3">{t10}{t11}{t12}</div></div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setOpen(false);
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== data) {
    t15 = data && <div className="grid gap-6"><RatingSummary average={data.score.rating} totalReviews={data.score.count} breakdown={[data.score.distribution.fiveStars, data.score.distribution.fourStars, data.score.distribution.threeStars, data.score.distribution.twoStars, data.score.distribution.oneStar]} />{data.ratings.map(_temp)}<Button as="a" href={TRUSTED_SHOPS_URL} target="_blank" rel="noopener noreferrer" variant="text" growChildren="none" IconStart={ArrowSquareOut} onClick={_temp2}>ALLE BEWERTUNGEN</Button></div>;
    $[25] = data;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== isOpen || $[28] !== t15) {
    t16 = <SimpleModal isOpen={isOpen} onClose={t14} size="medium" height="full" title="Bewertungen">{t15}</SimpleModal>;
    $[27] = isOpen;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== t13 || $[31] !== t16) {
    t17 = <div className="bg-secondary-dark-green"><div id="trusted-bar" className="fluid-container h-(--trusted-bar-height)">{t13}{t16}</div></div>;
    $[30] = t13;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
function _temp(rating) {
  return <ReviewCard key={rating.createdAt} authorName="Community Mitglied" country="Deutschland" rating={rating.rating} text={rating.comment} verified={rating.verificationType === "VERIFIED"} />;
}
function _temp2() {
  return trackClickCta("all reviews - modal", {
    type: "reviews"
  });
}